<template>
  <label class="base-input-shell" :class="{ error, disabled }">
    <slot />
  </label>
</template>

<script setup lang="ts">
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    disabled?: boolean
    error?: boolean
    bgColor?: string
    bgColorHover?: string
    bgColorActive?: string
    bgColorError?: string
    bgColorDisabled?: string
    borderColor?: string
    borderColorHover?: string
    focusBorderColor?: string
    borderColorError?: string
    borderColorDisabled?: string
    borderRadius?: string
    minHeight?: string
    maxWidth?: string
    padding?: string
    gap?: string
  }>(),
  {
    disabled: false,
    error: false,
    bgColor: undefined,
    bgColorHover: undefined,
    bgColorActive: undefined,
    bgColorError: undefined,
    bgColorDisabled: undefined,
    borderColor: undefined,
    borderColorHover: undefined,
    focusBorderColor: undefined,
    borderColorError: undefined,
    borderColorDisabled: undefined,
    borderRadius: undefined,
    minHeight: undefined,
    maxWidth: undefined,
    padding: undefined,
    gap: undefined,
  },
)

// Default DS settings with ability to overwrite from outer scope
const overrideBgColor = computed(() => props.bgColor || 'var(--color-input-background-default)')
const overrideBgColorHover = computed(
  () => props.bgColorHover || 'var(--color-input-background-hover)',
)
const overrideBgColorActive = computed(
  () => props.bgColorActive || 'var(--color-input-background-active)',
)
const overrideBgColorError = computed(
  () => props.bgColorError || 'var(--color-input-background-error)',
)
const overrideBgColorDisabled = computed(
  () => props.bgColorDisabled || 'var(--color-input-background-disabled)',
)
const overrideBorderColor = computed(() => props.borderColor || 'var(--color-input-border)')
const overrideBorderColorHover = computed(
  () => props.borderColorHover || 'var(--color-input-border-hover)',
)
const overrideFocusBorderColor = computed(
  () => props.focusBorderColor || 'var(--color-input-border-active)',
)
const overrideBorderColorError = computed(
  () => props.borderColorError || 'var(--color-input-border-error)',
)
const overrideBorderColorDisabled = computed(
  () => props.borderColorDisabled || 'var(--color-input-border-disabled)',
)
const overrideBorderRadius = computed(() => props.borderRadius || 'var(--radius-xs)')
const overrideMinHeight = computed(() => props.minHeight || '74px')
const overrideMaxWidth = computed(() => props.maxWidth || '100%')
const overridePadding = computed(() => props.padding || 'var(--space-8) var(--space-12)')
const overrideGap = computed(() => props.gap || 'var(--space-12)')
</script>

<style scoped lang="scss">
.base-input-shell {
  margin-left: auto;
  margin-right: auto;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  min-height: v-bind(overrideMinHeight);
  width: 100%;
  max-width: v-bind(overrideMaxWidth);
  padding: v-bind(overridePadding);
  gap: v-bind(overrideGap);
  border: 1px solid v-bind(overrideBorderColor);
  border-radius: v-bind(overrideBorderRadius);
  background: v-bind(overrideBgColor);
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease,
    box-shadow 0.2s ease;

  &:hover:not(.disabled):not(.error):not(:focus-within) {
    background: v-bind(overrideBgColorHover);
    border-color: v-bind(overrideBorderColorHover);
    box-shadow: var(--shadow-inner-input-hover);
  }

  &:focus-within:not(.disabled):not(.error) {
    border-color: v-bind(overrideFocusBorderColor);
    background: v-bind(overrideBgColorActive);
  }

  &.error {
    border-color: v-bind(overrideBorderColorError);
    background: v-bind(overrideBgColorError);
  }

  &.disabled {
    background: v-bind(overrideBgColorDisabled);
    border-color: v-bind(overrideBorderColorDisabled);
    pointer-events: none;
  }
}
</style>
